import { Injectable, computed, signal } from '@angular/core';

/** Visual theme for a background operation's toast/result modal, chosen by the dispatcher (e.g. green for create, red for delete). */
export type BackgroundOperationColorProfile = 'green' | 'red';

/** A single label/value row shown in the result modal's details table. */
export interface BackgroundOperationResultDetail {
  label: string;
  value: string;
}

/** The outcome of a finished background operation, shown in the global result modal. */
export interface BackgroundOperationResult {
  /** The id of the operation that produced this result (matches BackgroundOperationOptions.id). */
  operationId: string;
  title: string;
  message: string;
  isError?: boolean;
  details?: BackgroundOperationResultDetail[];
  /** Set automatically from the originating BackgroundOperationOptions.colorProfile. */
  colorProfile?: BackgroundOperationColorProfile;
}

/** A pending background operation, shown as a non-blocking corner toast. */
export interface BackgroundOperationPending {
  id: string;
  message: string;
  subMessage?: string;
  colorProfile: BackgroundOperationColorProfile;
}

/**
 * Pure signal-backed store for background-operation state (pending
 * operations + last result). Holds no timers/intervals and knows nothing
 * about how operations run — that orchestration lives in
 * BackgroundOperationRunner, which reads/writes this store. Kept separate so
 * the toast/result-modal components can depend on state alone, without
 * pulling in the runner's polling machinery.
 */
@Injectable({ providedIn: 'root' })
export class BackgroundOperationState {
  private readonly pending = signal<Map<string, BackgroundOperationPending>>(new Map());
  private readonly _result = signal<BackgroundOperationResult | null>(null);

  readonly result = this._result.asReadonly();
  readonly pendingOperations = computed(() => Array.from(this.pending().values()));
  readonly anyPending = computed(() => this.pending().size > 0);

  isPending(id: string): boolean {
    return this.pending().has(id);
  }

  setResult(result: BackgroundOperationResult | null): void {
    this._result.set(result);
  }

  addPending(pending: BackgroundOperationPending): void {
    const next = new Map(this.pending());
    next.set(pending.id, pending);
    this.pending.set(next);
  }

  removePending(id: string): void {
    const next = new Map(this.pending());
    next.delete(id);
    this.pending.set(next);
  }
}
